import { Header, Sidebar } from '@/components/layout';
import {
  BookingSheet,
  PatientLetterSheet,
  PrescriptionSheet,
  RequestLetterSheet,
  requestLetterTypeForDbType,
} from '@/components/session/task-sheets';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { hasVerkefniAccess, useAuth } from '@/contexts/AuthContext';
import { BloodTestOrderSheet } from '@/features/blood-tests/components/BloodTestOrderSheet';
import { type TodoFilters, type TodoItem, useDeleteTodo, useToggleTodoCompletion, useUserTodoList } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { format, isToday, isYesterday, parseISO, startOfDay } from 'date-fns';
import { is } from 'date-fns/locale';
import {
  AlertCircle,
  CalendarDays,
  Calendar as CalendarIcon,
  CheckCircle2,
  Circle,
  ClipboardList,
  Clock,
  Droplets,
  FileCheck,
  FileText,
  Filter,
  ListTodo,
  Mail,
  MessageSquareText,
  Pill,
  RefreshCw,
  Scan,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

// Current todo types: labels and icons; also the type filter's options, in this order.
const TODO_TYPES: Record<string, { label: string; icon: typeof Pill }> = {
  lyfseðill: { label: 'Lyfseðill', icon: Pill },
  endurbókun: { label: 'Endurbókun', icon: CalendarIcon },
  atvinnurekandavottorð: { label: 'Vottorð', icon: FileCheck },
  samantektarbréf: { label: 'Samantektarbréf', icon: Mail },
  blóðprufur: { label: 'Blóðprufur', icon: Droplets },
  röntgenmyndir: { label: 'Röntgenbeiðni', icon: Scan },
  tilvísun: { label: 'Tilvísun', icon: FileText },
  ráðgjafabeiðni: { label: 'Ráðgjafabeiðni', icon: MessageSquareText },
  almennt_verkefni: { label: 'Almennt verkefni', icon: ListTodo },
};

// Legacy/fallback types: still rendered, not offered as filters.
const TODO_TYPE_CONFIG: Record<string, { label: string; icon: typeof Pill }> = {
  ...TODO_TYPES,
  prescription: { label: 'Lyfseðill', icon: Pill },
  booking: { label: 'Endurbókun', icon: CalendarIcon },
  certificate: { label: 'Vottorð', icon: FileCheck },
  communication: { label: 'Samskipti', icon: Mail },
};

function getIcon(type: string) {
  return TODO_TYPE_CONFIG[type]?.icon || FileText;
}

// Group todos by date
interface DateGroup {
  label: string;
  date: Date;
  todos: TodoItem[];
}

function groupTodosByDate(todos: TodoItem[]): DateGroup[] {
  const groups = new Map<string, { date: Date; todos: TodoItem[] }>();

  for (const todo of todos) {
    const date = startOfDay(parseISO(todo.created_at));
    const key = date.toISOString();

    if (!groups.has(key)) {
      groups.set(key, { date, todos: [] });
    }
    groups.get(key)?.todos.push(todo);
  }

  // Sort groups by date (newest first)
  const sortedGroups = Array.from(groups.values()).sort((a, b) => b.date.getTime() - a.date.getTime());

  // Format date labels
  return sortedGroups.map(({ date, todos }) => {
    let label: string;
    if (isToday(date)) {
      label = `Í dag, ${format(date, 'd. MMMM', { locale: is })}`;
    } else if (isYesterday(date)) {
      label = `Í gær, ${format(date, 'd. MMMM', { locale: is })}`;
    } else {
      label = format(date, 'EEEE, d. MMMM', { locale: is });
      // Capitalize first letter
      label = label.charAt(0).toUpperCase() + label.slice(1);
    }
    return { label, date, todos };
  });
}

// Filter status options
type StatusFilter = 'all' | 'pending' | 'completed';

// The current todo hook type still requires user_id, but lib/api strips this key before sending.
const SESSION_SCOPED_TODO_USER_ID_SENTINEL = 1;

export default function TasksPage() {
  const { user } = useAuth();
  const hasAccess = hasVerkefniAccess(user);
  const queryClient = useQueryClient();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Task editing state
  const [editingBloodTest, setEditingBloodTest] = useState<TodoItem | null>(null);
  const [editingPrescription, setEditingPrescription] = useState<TodoItem | null>(null);
  const [editingBooking, setEditingBooking] = useState<TodoItem | null>(null);
  const [editingPatientLetter, setEditingPatientLetter] = useState<TodoItem | null>(null);
  const [editingRequest, setEditingRequest] = useState<TodoItem | null>(null);
  const editingRequestType = editingRequest ? requestLetterTypeForDbType(editingRequest.type) : undefined;

  // Build filters for API
  const filters: TodoFilters | undefined = useMemo(() => {
    if (!user) return undefined;

    const f: TodoFilters = { user_id: SESSION_SCOPED_TODO_USER_ID_SENTINEL };

    if (statusFilter === 'pending') {
      f.completed = false;
    } else if (statusFilter === 'completed') {
      f.completed = true;
    }

    if (typeFilter && typeFilter !== 'all') {
      f.type = typeFilter;
    }

    if (dateRange?.from) {
      f.date_from = dateRange.from.toISOString();
    }
    if (dateRange?.to) {
      f.date_to = dateRange.to.toISOString();
    }

    return f;
  }, [user, statusFilter, typeFilter, dateRange]);

  // Fetch todos - refetch on mount and window focus to catch updates from other pages
  const { data, isLoading, error, refetch } = useUserTodoList(filters, {
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale so it refetches
  });

  // Toggle mutation
  const toggleMutation = useToggleTodoCompletion({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] });
    },
  });

  const deleteMutation = useDeleteTodo({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] });
    },
  });

  const handleDelete = (todo: TodoItem) => {
    if (deleteMutation.isPending || todo.has_referral_attempt) return;
    deleteMutation.mutate({ id: todo.id, user_id: SESSION_SCOPED_TODO_USER_ID_SENTINEL });
  };

  // Group todos by date (filtering happens server-side via `filters`)
  const groupedTodos = useMemo(() => groupTodosByDate(data?.todo_items ?? []), [data?.todo_items]);

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || typeFilter !== 'all' || dateRange !== undefined;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('all');
    setTypeFilter('all');
    setDateRange(undefined);
  };

  // Handle toggle
  const handleToggle = (todo: TodoItem) => {
    if (toggleMutation.isPending || todo.has_referral_attempt) return;
    toggleMutation.mutate({
      id: todo.id,
      completed: !todo.completed,
      user_id: SESSION_SCOPED_TODO_USER_ID_SENTINEL,
    });
  };

  // Handle task card click - open appropriate editor
  const handleTaskClick = (todo: TodoItem) => {
    const type = todo.type;
    if (type === 'blóðprufur') {
      setEditingBloodTest(todo);
    } else if (type === 'lyfseðill') {
      setEditingPrescription(todo);
    } else if (type === 'endurbókun') {
      setEditingBooking(todo);
    } else if (type === 'samantektarbréf') {
      setEditingPatientLetter(todo);
    } else if (requestLetterTypeForDbType(type)) {
      setEditingRequest(todo);
    }
  };

  // Check if a task type has an editor
  const hasEditor = (type: string) => {
    return (
      ['blóðprufur', 'lyfseðill', 'endurbókun', 'samantektarbréf'].includes(type) ||
      requestLetterTypeForDbType(type) !== undefined
    );
  };

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange?.from) return 'Veldu dagsetningu';
    if (!dateRange.to) {
      return format(dateRange.from, 'd. MMM yyyy', { locale: is });
    }
    return `${format(dateRange.from, 'd. MMM', { locale: is })} - ${format(dateRange.to, 'd. MMM yyyy', { locale: is })}`;
  };

  if (!hasAccess) {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Header title="Verkefnalisti" />
          <main className="flex flex-1 items-center justify-center bg-slate-50/30">
            <div className="text-center">
              <ClipboardList className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="mb-2 font-medium text-lg text-slate-700">Verkefnalisti er ekki virkur</h3>
              <p className="text-slate-500">Hafðu samband við kerfisstjóra til að virkja verkefnalista.</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Verkefnalisti" />

        <main className="flex-1 overflow-hidden bg-slate-50/30">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-4xl p-8">
              {/* Filter Bar */}
              <Card className="mb-6 border-slate-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-slate-500">
                      <Filter className="h-4 w-4" />
                      <span className="font-medium text-sm">Sía:</span>
                    </div>

                    {/* Status Filter */}
                    <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                      <SelectTrigger className="h-9 w-[130px]">
                        <SelectValue placeholder="Staða" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Allt</SelectItem>
                        <SelectItem value="pending">Óklárað</SelectItem>
                        <SelectItem value="completed">Lokið</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Type Filter */}
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="h-9 w-[150px]">
                        <SelectValue placeholder="Tegund" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Allar tegundir</SelectItem>
                        {Object.entries(TODO_TYPES).map(([value, { label }]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Date Range Picker */}
                    <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="h-9 px-3 font-normal">
                          <CalendarDays className="mr-2 h-4 w-4" />
                          {formatDateRange()}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="range"
                          selected={dateRange}
                          onSelect={(range) => {
                            setDateRange(range);
                            if (range?.to) {
                              setIsDatePickerOpen(false);
                            }
                          }}
                          numberOfMonths={2}
                        />
                      </PopoverContent>
                    </Popover>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="h-9 text-slate-500 hover:text-slate-700"
                      >
                        <X className="mr-1 h-4 w-4" />
                        Hreinsa
                      </Button>
                    )}

                    {/* Refresh Button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => refetch()}
                      className="ml-auto h-9 w-9"
                      disabled={isLoading}
                    >
                      <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Header */}
              <div className="mb-8">
                <h2 className="font-bold text-2xl text-slate-900">Yfirlit verkefna</h2>
                <p className="text-slate-500">
                  {data?.count !== undefined
                    ? `${data.count} verkefni fundust`
                    : 'Hér getur þú séð öll verkefni sem bíða úrvinnslu eða hafa verið kláruð.'}
                </p>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Spinner className="h-8 w-8 text-primary" />
                </div>
              )}

              {/* Error State */}
              {error && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 text-red-600">
                      <AlertCircle className="h-5 w-5" />
                      <div>
                        <p className="font-medium">Villa við að sækja verkefni</p>
                        <p className="text-red-500 text-sm">{error.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {!isLoading && !error && groupedTodos.length === 0 && (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <ClipboardList className="mx-auto mb-4 h-12 w-12 text-slate-300" />
                    <h3 className="mb-2 font-medium text-lg text-slate-700">Engin verkefni fundust</h3>
                    <p className="text-slate-500">
                      {hasActiveFilters
                        ? 'Prófaðu að breyta síunum til að sjá fleiri verkefni.'
                        : 'Verkefni birtast hér þegar þau eru búin til úr samantektum.'}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Task Groups */}
              {!isLoading && !error && groupedTodos.length > 0 && (
                <div className="space-y-10">
                  {groupedTodos.map((group) => (
                    <div key={group.date.toISOString()} className="space-y-4">
                      <h3 className="flex items-center gap-2 font-semibold text-slate-500 text-sm uppercase tracking-wider">
                        <CalendarIcon className="h-4 w-4" />
                        {group.label}
                      </h3>

                      <div className="grid gap-3">
                        {group.todos.map((todo) => {
                          const Icon = getIcon(todo.type);
                          const isCompleted = todo.completed;
                          const patientName = todo.patient
                            ? `${todo.patient.first_name} ${todo.patient.last_name}`
                            : 'Enginn sjúklingur';
                          const time = format(parseISO(todo.created_at), 'HH:mm');

                          const clickable = hasEditor(todo.type);

                          return (
                            <Card
                              key={todo.id}
                              className={`group overflow-hidden border border-slate-200 bg-white transition-all hover:border-primary/30 ${
                                clickable ? 'cursor-pointer hover:shadow-md' : ''
                              }`}
                              onClick={clickable ? () => handleTaskClick(todo) : undefined}
                            >
                              <CardContent className="p-0">
                                <div className="flex items-center gap-4 p-4">
                                  <div
                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                      isCompleted
                                        ? 'bg-green-50 text-green-600'
                                        : 'bg-slate-100 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary'
                                    }`}
                                  >
                                    <Icon className="h-5 w-5" />
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <h4 className="mb-1 truncate font-semibold text-slate-900">{todo.title}</h4>
                                    <div className="flex items-center gap-3 text-slate-500 text-xs">
                                      <div className="flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        {patientName}
                                      </div>
                                      <span>•</span>
                                      <div className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {time}
                                      </div>
                                      {todo.description && (
                                        <>
                                          <span>•</span>
                                          <span className="max-w-[200px] truncate">
                                            {todo.description.split('\n')[0]}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  <span
                                    className={`shrink-0 rounded-md px-2 py-0.5 font-medium text-[10px] ${
                                      isCompleted ? 'bg-slate-100 text-slate-600' : 'bg-primary text-white'
                                    }`}
                                  >
                                    {isCompleted ? 'Lokið' : 'Drög'}
                                  </span>

                                  <div className="relative z-10 flex shrink-0 items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggle(todo);
                                      }}
                                      aria-label={`Merkja ${todo.title} ${isCompleted ? 'ólokið' : 'lokið'}`}
                                      title={
                                        todo.has_referral_attempt ? 'Staða tilvísunar er stýrð af sendingu.' : undefined
                                      }
                                      disabled={toggleMutation.isPending || todo.has_referral_attempt}
                                      className={`pointer-events-auto flex h-10 w-10 cursor-pointer select-none items-center justify-center rounded-full border-2 transition-all ${
                                        isCompleted
                                          ? 'border-green-300 bg-green-50 text-green-600 hover:bg-green-100'
                                          : 'border-slate-300 bg-white hover:border-primary hover:text-primary'
                                      } ${toggleMutation.isPending ? 'cursor-not-allowed opacity-50' : ''}`}
                                    >
                                      {isCompleted ? (
                                        <CheckCircle2 className="pointer-events-none h-5 w-5" />
                                      ) : (
                                        <Circle className="pointer-events-none h-5 w-5" />
                                      )}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDelete(todo);
                                      }}
                                      aria-label={`Eyða ${todo.title}`}
                                      title={
                                        todo.has_referral_attempt
                                          ? 'Tilvísun með sendingarsögu er varðveitt.'
                                          : undefined
                                      }
                                      disabled={deleteMutation.isPending || todo.has_referral_attempt}
                                      className="pointer-events-auto flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-slate-400 transition-all hover:bg-red-50 hover:text-red-500"
                                    >
                                      <Trash2 className="pointer-events-none h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </main>
      </div>

      {/* Task Editing Sheets - shared components */}
      <BloodTestOrderSheet
        task={
          editingBloodTest
            ? {
                todoId: editingBloodTest.id,
                details: editingBloodTest.description,
                extraData: editingBloodTest.extra_data,
                patient: editingBloodTest.patient
                  ? {
                      ssn: editingBloodTest.patient.ssn ?? '',
                      name: `${editingBloodTest.patient.first_name} ${editingBloodTest.patient.last_name}`.trim(),
                    }
                  : undefined,
              }
            : null
        }
        onClose={() => setEditingBloodTest(null)}
        onOrdered={() => editingBloodTest && !editingBloodTest.completed && handleToggle(editingBloodTest)}
      />

      <PrescriptionSheet
        open={!!editingPrescription}
        onOpenChange={(open) => !open && setEditingPrescription(null)}
        initialMedicationName={editingPrescription?.title}
        initialDosageInfo={editingPrescription?.description}
        onComplete={() => editingPrescription && handleToggle(editingPrescription)}
      />

      <BookingSheet
        open={!!editingBooking}
        onOpenChange={(open) => !open && setEditingBooking(null)}
        suggestedDate={editingBooking?.description?.match(/\d{4}-\d{2}-\d{2}/)?.[0]}
        suggestedTime={editingBooking?.description?.match(/\d{2}:\d{2}/)?.[0]}
        onComplete={() => editingBooking && handleToggle(editingBooking)}
      />

      <PatientLetterSheet
        open={!!editingPatientLetter}
        onOpenChange={(open) => !open && setEditingPatientLetter(null)}
        initialContent={editingPatientLetter?.description}
        todoId={editingPatientLetter?.id}
        patientName={
          editingPatientLetter?.patient
            ? `${editingPatientLetter.patient.first_name} ${editingPatientLetter.patient.last_name}`
            : undefined
        }
        onComplete={() => editingPatientLetter && handleToggle(editingPatientLetter)}
      />

      <RequestLetterSheet
        open={!!editingRequest}
        onOpenChange={(open) => !open && setEditingRequest(null)}
        requestType={editingRequestType ?? 'referral'}
        initialTitle={editingRequest?.title}
        initialDetails={editingRequest?.description}
        initialExtraData={editingRequest?.extra_data}
        todoId={editingRequest?.id}
        onContentSaved={(content, extraData) => {
          setEditingRequest((prev) => (prev ? { ...prev, description: content, extra_data: extraData } : prev));
          queryClient.invalidateQueries({ queryKey: ['todos', 'user-list'] });
        }}
        onComplete={() => editingRequest && handleToggle(editingRequest)}
      />
    </div>
  );
}
